import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useEffect, useRef } from "react";
import { LoadingEditRead } from "src/store/api/generatedApi";
import { z } from "zod";

dayjs.extend(utc);

const SPAN_START_FORMAT = "MMM D HH:mm:ss";
const SPAN_END_FORMAT = "HH:mm:ss";
const AUTHORED_FORMAT = "YYYY-MM-DD HH:mm";

export type ReviewMark = "approved" | "declined";

const PANE_STATES = ["expanded", "collapsed"] as const;
type PaneState = (typeof PANE_STATES)[number];

const spanLabel = (edit: LoadingEditRead): string =>
  `${dayjs.utc(edit.start_time).format(SPAN_START_FORMAT)}–${dayjs
    .utc(edit.end_time)
    .format(SPAN_END_FORMAT)}`;

const assignmentLabel = (edit: LoadingEditRead, idle: string): string =>
  edit.target_status === "loading"
    ? `${edit.target_heat_id ?? "—"} · ${edit.target_basket_number ?? "—"}`
    : idle;

export const LoadingReviewPane = ({
  proposals,
  marks,
  amendedIds,
  selectedId,
  decidedCount,
  submitting,
  onMark,
  onMarkAll,
  onSelect,
  onSubmit,
}: {
  readonly proposals: readonly LoadingEditRead[];
  readonly marks: ReadonlyMap<number, ReviewMark>;
  readonly amendedIds: ReadonlySet<number>;
  readonly selectedId: number | null;
  readonly decidedCount: number;
  readonly submitting: boolean;
  readonly onMark: (editId: number, mark: ReviewMark) => void;
  readonly onMarkAll: (mark: ReviewMark) => void;
  readonly onSelect: (editId: number) => void;
  readonly onSubmit: () => void;
}) => {
  const { t } = useTenantTranslation();
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const [paneState, setPaneState] = useLocalStorageState<PaneState>(
    "loadingWindowEditorReviewPane",
    "expanded",
    { schema: z.enum(PANE_STATES) }
  );
  const open = paneState === "expanded";

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedId, open]);

  if (!open) {
    return (
      <Stack
        alignItems="center"
        sx={{
          flexShrink: 0,
          py: 1,
          border: 1,
          borderColor: "grey.300",
          borderRadius: 1,
        }}
      >
        <Tooltip title={t("show")} placement="left">
          <ButtonBase
            aria-label={t("show")}
            onClick={() => {
              setPaneState("expanded");
            }}
            sx={{ flexDirection: "column", gap: 1, borderRadius: 1, p: 0.5 }}
          >
            <ChevronLeftRoundedIcon />
            <Typography
              variant="body2"
              sx={{
                writingMode: "vertical-rl",
                fontWeight: "fontWeightMedium",
              }}
            >
              {t("pendingReviewCollapsedSummary", {
                pending: proposals.length,
                toSubmit: decidedCount,
              })}
            </Typography>
          </ButtonBase>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: 320,
        flexShrink: 0,
        border: 1,
        borderColor: "grey.300",
        borderRadius: 1,
        minHeight: 0,
      }}
    >
      <Stack
        spacing={0.5}
        sx={{ p: 1, borderBottom: 1, borderColor: "grey.300" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center">
            <Tooltip title={t("hide")}>
              <IconButton
                size="small"
                aria-label={t("hide")}
                onClick={() => {
                  setPaneState("collapsed");
                }}
              >
                <ChevronRightRoundedIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body1" sx={{ fontWeight: "fontWeightMedium" }}>
              {t("pendingReview")}
            </Typography>
          </Stack>
          <Button
            variant="contained"
            size="small"
            disabled={decidedCount === 0 || submitting}
            onClick={onSubmit}
          >
            {t("submitReview")} ({decidedCount})
          </Button>
        </Stack>
        {proposals.length > 0 ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              color="success"
              onClick={() => {
                onMarkAll("approved");
              }}
            >
              {t("acceptUnreviewed")}
            </Button>
            <Button
              size="small"
              color="error"
              onClick={() => {
                onMarkAll("declined");
              }}
            >
              {t("rejectUnreviewed")}
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {proposals.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {t("noPendingEdits")}
        </Typography>
      ) : (
        <Box sx={{ overflow: "auto" }}>
          {proposals.map((edit) => {
            const mark = marks.get(edit.id);
            const amended = amendedIds.has(edit.id);
            const isSelected = edit.id === selectedId;
            return (
              <Stack
                key={edit.id}
                ref={isSelected ? selectedRef : undefined}
                onClick={() => {
                  onSelect(edit.id);
                }}
                sx={{
                  p: 1,
                  borderBottom: 1,
                  borderColor: "grey.200",
                  borderLeft: 3,
                  borderLeftColor: isSelected ? "primary.main" : "grey.200",
                  cursor: "pointer",
                  bgcolor: (theme) =>
                    amended
                      ? alpha(theme.palette.warning.main, 0.18)
                      : mark === "approved"
                        ? alpha(theme.palette.success.main, 0.18)
                        : mark === "declined"
                          ? alpha(theme.palette.error.main, 0.18)
                          : undefined,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      color="success"
                      sx={{
                        width: 28,
                        height: 28,
                        border: 1,
                        borderColor: "grey.400",
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onMark(edit.id, "approved");
                      }}
                    >
                      <CheckRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      sx={{
                        width: 28,
                        height: 28,
                        border: 1,
                        borderColor: "grey.400",
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onMark(edit.id, "declined");
                      }}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="body2Mono" noWrap>
                      {edit.area_name}
                      {amended ? ` · ${t("edited")}` : ""}
                    </Typography>
                    <Typography variant="body2Mono" noWrap>
                      {assignmentLabel(edit, t("idle"))}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {spanLabel(edit)} · {edit.source}
                    </Typography>
                  </Stack>
                </Stack>

                <Collapse in={isSelected} mountOnEnter unmountOnExit>
                  <Stack spacing={0.5} sx={{ pl: 1, pt: 0.5 }}>
                    {edit.rationale ? (
                      <Typography variant="body2">{edit.rationale}</Typography>
                    ) : null}
                    <Typography variant="body2" color="text.secondary">
                      {edit.created_by?.name ??
                        edit.created_by?.email ??
                        edit.source}
                      {" · "}
                      {dayjs.utc(edit.created_at).format(AUTHORED_FORMAT)}
                    </Typography>
                  </Stack>
                </Collapse>
              </Stack>
            );
          })}
        </Box>
      )}
    </Stack>
  );
};
